import { portableSceneToEditorDocument } from '../public-format/portable-scene-adapter';
import { unpackPortableScene } from '../public-format/portable-scene-package';
import { portableSceneDiagnostics, PORTABLE_SCENE_VERSION } from '../public-format/portable-scene';
import { unzip } from 'fflate';
import { decodeMaipoJsonBytes, migrateMaipoPublicScene, publicSceneDiagnostics, unpackMaipoPublicScene, type MaipoPublicSceneV01 } from '@maipo/format';
import { DEFAULT_LIMITS, validateDimensions, walkLayers, type ImportNotice } from '../core/model';
import { MaipoProjectCodec, preflightMaipoArchive, serializeProject } from '../persistence/project-format';
import { publicSceneToEditorDocument } from '../public-format/adapter';
import { prepareNativeDocumentFonts } from '../public-format/native-fonts';
import { throwIfAborted, type CodecContext, type CodecReadResult, type DocumentCodec } from './types';

const MAX_PUBLIC_JSON_BYTES = 64 * 1024 * 1024;
const MAX_DISPATCH_MANIFEST_BYTES = 32 * 1024 * 1024;
const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024;

/** Classify only; the chosen reader still validates its entire archive contract. */
async function archiveFormat(bytes: Uint8Array): Promise<{ format?: unknown; version?: unknown }> {
  preflightMaipoArchive(bytes);
  const manifest = await new Promise<Uint8Array>((resolve, reject) => {
    unzip(bytes, { filter: file => file.name === 'manifest.json' && file.originalSize <= MAX_DISPATCH_MANIFEST_BYTES }, (error, entries) => {
      if (error) reject(error);
      else if (!entries['manifest.json']) reject(new Error('Maipo archive has no bounded manifest.json.'));
      else resolve(entries['manifest.json']);
    });
  });
  const value = decodeMaipoJsonBytes(manifest, { maxBytes: MAX_DISPATCH_MANIFEST_BYTES, maxCodeUnits: MAX_DISPATCH_MANIFEST_BYTES });
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export class MaipoDocumentCodec implements DocumentCodec {
  readonly id = 'maipo';
  readonly label = 'Maipo document';
  private readonly native = new MaipoProjectCodec();

  canRead(file: File): boolean {
    return this.native.canRead(file) || /\.json$/i.test(file.name) || file.type === 'application/json';
  }

  async read(file: File, context: CodecContext = {}): Promise<CodecReadResult> {
    throwIfAborted(context.signal);
    if (file.size > MAX_FILE_BYTES) throw new Error('Maipo document exceeds the 2 GiB file limit.');
    const prefix = new Uint8Array(await file.slice(0, 2).arrayBuffer());
    const packed = prefix[0] === 0x50 && prefix[1] === 0x4b;
    if (!packed && file.size > MAX_PUBLIC_JSON_BYTES) throw new Error('Public scene JSON exceeds the 64 MiB limit.');
    const bytes = new Uint8Array(await file.arrayBuffer());
    throwIfAborted(context.signal);
    context.onProgress?.({ phase: 'validating', progress: 0.1, message: 'Checking Maipo document' });
    let scene: MaipoPublicSceneV01;
    const notices: ImportNotice[] = [];
    const openPortable = async (value: unknown): Promise<CodecReadResult> => {
      context.onProgress?.({ phase: 'converting', progress: 0.5, message: 'Verifying draft portable text' });
      const diagnostics: ImportNotice[] = portableSceneDiagnostics(value).map(item => ({ severity: item.severity, feature: item.code, message: item.message,
        handling: item.code === 'non-portable-system-font' || item.code === 'unbound-text-font' ? 'approximated' : 'preserved' }));
      const document = await portableSceneToEditorDocument(value, context.signal);
      document.source = { kind: 'maipo', name: file.name };
      const report = { codec: 'Maipo portable scene draft', sourceName: file.name, notices: [{ severity: 'info' as const, feature: 'draft-portable-scene', handling: 'preserved' as const, message: 'Opened unpublished scene 0.2-draft with recomputed portable text. Published 0.1 readers do not support this scene.' }, ...diagnostics] };
      document.importReport = report;
      return { document, report };
    };
    if (packed) {
      const { format, version } = await archiveFormat(bytes);
      throwIfAborted(context.signal);
      if (format === 'maipo-project' || format === 'vireo-project') return this.native.readBytes(bytes, file.name, context);
      if (format !== 'maipo-package') throw new Error('Unsupported Maipo archive format.');
      if (version === PORTABLE_SCENE_VERSION) return openPortable(await unpackPortableScene(bytes, context.signal));
      scene = await unpackMaipoPublicScene(bytes, context.signal);
    } else {
      const value = decodeMaipoJsonBytes(bytes, { maxBytes: MAX_PUBLIC_JSON_BYTES, maxCodeUnits: MAX_PUBLIC_JSON_BYTES });
      if (value && typeof value === 'object' && !Array.isArray(value) && (value as Record<string, unknown>).version === PORTABLE_SCENE_VERSION) return openPortable(value);
      const migration = migrateMaipoPublicScene(value);
      scene = migration.scene;
      if (migration.fromVersion !== migration.toVersion) notices.push({
        severity: 'info', feature: 'scene-migration', handling: 'preserved',
        message: `Migrated public scene ${migration.fromVersion} to ${migration.toVersion} with its registered migration.`,
      });
    }
    throwIfAborted(context.signal);
    // Adapter admission rejects unknown required semantics and unsupported node
    // kinds before document/font activation. No rasterized substitute is opened.
    const document = publicSceneToEditorDocument(scene);
    validateDimensions(document.width, document.height);
    if (walkLayers(document.root).length > DEFAULT_LIMITS.maxLayers) throw new Error('Public scene exceeds the Studio layer limit.');
    document.source = { kind: 'maipo', name: file.name };
    for (const diagnostic of publicSceneDiagnostics(scene)) notices.push({
      severity: diagnostic.severity, feature: diagnostic.code, message: diagnostic.message,
      handling: diagnostic.code === 'non-portable-system-font' || diagnostic.code === 'unbound-text-font' ? 'approximated' : 'preserved',
    });
    const report = notices.length ? { codec: 'Maipo public scene', sourceName: file.name, notices } : undefined;
    if (report) document.importReport = report;
    // Admit only projections that can be saved by Studio's native writer. This
    // also applies the existing native JSON/depth limits to retained resources.
    context.onProgress?.({ phase: 'converting', progress: 0.6, message: 'Preparing editable scene' });
    await serializeProject(document, context.signal);
    await prepareNativeDocumentFonts(document, context.signal);
    context.onProgress?.({ phase: 'converting', progress: 1, message: 'Scene ready' });
    return { document, ...(report ? { report } : {}) };
  }
}
